import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, SlidersHorizontalIcon } from 'lucide-react';
import type { Product } from '../../types/product';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { discountPercent } from '../../utils/products';
export function ProductCard({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [wished, setWished] = useState(false);
  const discount = discountPercent(product);
  const href = `/product/${product.id}`;
  const swatches = product.variants?.find(g => g.kind === 'swatch');
  const hasPriceVariants = product.variants?.some(g => g.options.some(o => o.priceDelta > 0));
  const optionHint = product.variants?.find(g => g.kind !== 'swatch');
  return <article className="group flex h-full flex-col rounded-xl border border-line bg-white p-3 transition-[border-color,box-shadow] duration-200 hover:border-ink/20 hover:shadow-[0_10px_28px_-14px_rgba(14,26,36,0.22)]" data-id="element-307" data-mp="2k6ojqxcgynf4">
      <div className="relative aspect-square overflow-hidden rounded-lg bg-surface" data-id="element-308" data-mp="916j1g3rwds">
        <Link to={href} tabIndex={-1} aria-hidden="true" data-id="element-309" data-mp="3rh6q658wwd61">
          <img src={product.image} alt="" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-310" data-mp="3mh0wykmzuilu" />
        </Link>
        <div className="pointer-events-none absolute left-2.5 top-2.5 flex gap-1.5" data-id="element-311" data-mp="j0k8r62az6er">
          {discount > 0 && <span className="rounded-md bg-sale px-2 py-1 text-[11px] font-bold text-white" data-id="element-312" data-mp="e929qnjubfwf">−{discount}%</span>}
          {product.badge && <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${product.badge === 'New' ? 'bg-volt text-ink' : 'bg-ink text-white'}`} data-id="element-313" data-mp="99zf7x1hks2p">
              {product.badge}
            </span>}
        </div>
        <button type="button" onClick={() => setWished(w => !w)} aria-pressed={wished} aria-label={wished ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`} className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-white text-ink transition-transform duration-150 active:scale-90" data-id="element-314" data-mp="11x3fjopmwi85">
          <HeartIcon className={`h-4 w-4 ${wished ? 'fill-sale text-sale' : ''}`} data-id="element-315" data-mp="2rdyyxgqdsrya" />
        </button>
      </div>

      <div className="flex flex-1 flex-col px-1 pt-3.5" data-id="element-316" data-mp="2hi4au63e24o4">
        <div className="flex items-center justify-between gap-2" data-id="element-317" data-mp="2hjp4lthvpjkx">
          <p className="truncate text-xs font-medium text-muted" data-id="element-318" data-mp="23j4nd1rm3vtj">{product.brand}</p>
          {swatches && <span className="flex gap-1" aria-label={`${swatches.options.length} colours`} data-id="element-319" data-mp="28ih6pcj9ykrd">
              {swatches.options.map(o => <span key={o.id} className="h-3 w-3 rounded-full ring-1 ring-ink/15" style={{
            backgroundColor: o.swatch
          }} data-id="element-320" data-mp="1oww2fn2lt1f" />)}
            </span>}
        </div>
        <h3 className="mt-1 line-clamp-2 min-h-[2.5rem] text-[15px] font-semibold leading-5 text-ink" data-id="element-321" data-mp="gu9ftmkwiwan">
          <Link to={href} className="hover:underline" data-id="element-322" data-mp="bwrfgkky9pe9">
            {product.name}
          </Link>
        </h3>

        <ul className="mt-2.5 flex gap-1.5 overflow-hidden" aria-label="Key specs" data-id="element-323" data-mp="pofnocs2vtpy">
          {product.specs.map(s => <li key={s} className="shrink-0 rounded-md bg-surface px-2 py-1 text-xs font-medium text-ink" data-id="element-324" data-mp="zk9261pcxxad">
              {s}
            </li>)}
        </ul>

        <div className="mt-2.5 flex items-center gap-1.5" data-id="element-325" data-mp="unimhfhgt3ph">
          <StarRating rating={product.rating} data-id="element-326" data-mp="18k5waiuinoct" />
          <span className="text-xs text-muted" data-id="element-327" data-mp="18iusij12mglp">
            {product.rating} ({product.reviews.toLocaleString('en-IE')})
          </span>
        </div>

        <div className="mt-auto pt-4" data-id="element-328" data-mp="3adlkza71mt55">
          <p className="h-4 text-[11px] font-medium text-muted" data-id="element-329" data-mp="3aemzjon9pxrm">
            {optionHint ? `${optionHint.options.length} ${optionHint.name.toLowerCase()} options` : ''}
          </p>
          <div className="flex items-baseline gap-2" data-id="element-330" data-mp="37run9f3xvnrs">
            <span className="text-xl font-bold tracking-tight text-ink" data-id="element-331" data-mp="3cqpdv385egmg">
              {hasPriceVariants && <span className="mr-1 text-xs font-medium text-muted" data-id="element-332" data-mp="3gsapymcuyqvl">from</span>}
              {formatPrice(product.price)}
            </span>
            {product.compareAt && <span className="text-sm text-muted line-through" data-id="element-333" data-mp="3lpsxq9f53020">{formatPrice(product.compareAt)}</span>}
          </div>
          <div className="mt-1" data-id="element-334" data-mp="2t37ykmsf9jnw">
            <StockLabel status={product.stock} data-id="element-335" data-mp="2t49lppmppwlz" />
          </div>
          {product.template === 'system' ? <Link to={href} className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-ink bg-white text-sm font-semibold text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-336" data-mp="2y1j80wet1s5l">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-337" data-mp="2ac8dvxrymw0f" />
              Configure
            </Link> : <button type="button" onClick={() => addItem(product)} className="mt-3 h-10 w-full rounded-lg bg-ink text-sm font-semibold text-white transition-[background-color,transform] duration-150 hover:bg-ink-soft active:scale-[0.98]" data-id="element-338" data-mp="22rwcftaeaotv">
              {product.stock === 'preorder' ? 'Pre-order' : 'Add to cart'}
            </button>}
        </div>
      </div>
    </article>;
}